/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator } from "@azure/core-paging";
import { Connectors } from "../operationsInterfaces";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { SecurityCenter } from "../securityCenter";
import {
  ConnectorSetting,
  ConnectorsListNextOptionalParams,
  ConnectorsListOptionalParams,
  ConnectorsListResponse,
  ConnectorsGetOptionalParams,
  ConnectorsGetResponse,
  ConnectorsCreateOrUpdateOptionalParams,
  ConnectorsCreateOrUpdateResponse,
  ConnectorsDeleteOptionalParams,
  ConnectorsListNextResponse
} from "../models";

/// <reference lib="esnext.asynciterable" />
/** Class containing Connectors operations. */
export class ConnectorsImpl implements Connectors {
  private readonly client: SecurityCenter;

  /**
   * Initialize a new instance of the class Connectors class.
   * @param client Reference to the service client
   */
  constructor(client: SecurityCenter) {
    this.client = client;
  }

  /**
   * Cloud accounts connectors of a subscription
   * @param options The options parameters.
   */
  public list(
    options?: ConnectorsListOptionalParams
  ): PagedAsyncIterableIterator<ConnectorSetting> {
    const iter = this.listPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: () => {
        return this.listPagingPage(options);
      }
    };
  }

  private async *listPagingPage(
    options?: ConnectorsListOptionalParams
  ): AsyncIterableIterator<ConnectorSetting[]> {
    let result = await this._list(options);
    yield result.value || [];
    let continuationToken = result.nextLink;
    while (continuationToken) {
      result = await this._listNext(continuationToken, options);
      continuationToken = result.nextLink;
      yield result.value || [];
    }
  }

  private async *listPagingAll(
    options?: ConnectorsListOptionalParams
  ): AsyncIterableIterator<ConnectorSetting> {
    for await (const page of this.listPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * Cloud accounts connectors of a subscription
   * @param options The options parameters.
   */
  private _list(
    options?: ConnectorsListOptionalParams
  ): Promise<ConnectorsListResponse> {
    return this.client.sendOperationRequest({ options }, listOperationSpec);
  }

  /**
   * Details of a specific cloud account connector
   * @param connectorName Name of the cloud account connector
   * @param options The options parameters.
   */
  get(
    connectorName: string,
    options?: ConnectorsGetOptionalParams
  ): Promise<ConnectorsGetResponse> {
    return this.client.sendOperationRequest(
      { connectorName, options },
      getOperationSpec
    );
  }

  /**
   * Create a cloud account connector or update an existing one. Connect to your cloud account. For AWS,
   * use either account credentials or role-based authentication. For GCP, use account organization
   * credentials.
   * @param connectorName Name of the cloud account connector
   * @param connectorSetting Settings for the cloud account connector
   * @param options The options parameters.
   */
  createOrUpdate(
    connectorName: string,
    connectorSetting: ConnectorSetting,
    options?: ConnectorsCreateOrUpdateOptionalParams
  ): Promise<ConnectorsCreateOrUpdateResponse> {
    return this.client.sendOperationRequest(
      { connectorName, connectorSetting, options },
      createOrUpdateOperationSpec
    );
  }

  /**
   * Delete a cloud account connector from a subscription
   * @param connectorName Name of the cloud account connector
   * @param options The options parameters.
   */
  delete(
    connectorName: string,
    options?: ConnectorsDeleteOptionalParams
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { connectorName, options },
      deleteOperationSpec
    );
  }

  /**
   * ListNext
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    nextLink: string,
    options?: ConnectorsListNextOptionalParams
  ): Promise<ConnectorsListNextResponse> {
    return this.client.sendOperationRequest(
      { nextLink, options },
      listNextOperationSpec
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Security/connectors",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ConnectorSettingList
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion11],
  urlParameters: [Parameters.$host, Parameters.subscriptionId],
  headerParameters: [Parameters.accept],
  serializer
};
const getOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Security/connectors/{connectorName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ConnectorSetting
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion11],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.connectorName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const createOrUpdateOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Security/connectors/{connectorName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.ConnectorSetting
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  requestBody: Parameters.connectorSetting,
  queryParameters: [Parameters.apiVersion11],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.connectorName
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer
};
const deleteOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Security/connectors/{connectorName}",
  httpMethod: "DELETE",
  responses: {
    200: {},
    204: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion11],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.connectorName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ConnectorSettingList
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion11],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.nextLink
  ],
  headerParameters: [Parameters.accept],
  serializer
};
